<script setup lang="ts">
import {
  effectiveAllergenText,
  makerDisclaimer,
  showsMakerDisclaimer,
} from "~/utils/effectiveAllergens";

// The ticket 1149 output rule, rendered once for every surface that declares a dish (cases A, B
// and C in utils/effectiveAllergens.ts). `line` decides case A: an order line, or a product
// carrying the customer's picks. `product` is whose tags and claims B and C read, `line` itself
// when omitted. The allergens slot lets the product modal keep its accordion for case B, and
// `showAllergens: false` drops the allergens line where a surface renders it elsewhere.
const props = withDefaults(
  defineProps<{
    line: Record<string, unknown> | null;
    product?: Record<string, unknown> | null;
    makerName?: string | null;
    tooltip?: boolean;
    showAllergens?: boolean;
  }>(),
  { product: undefined, makerName: null, tooltip: false, showAllergens: true },
);

const declared = computed(() => props.product ?? props.line);
</script>

<template>
  <div class="allergen-declaration">
    <p
      v-if="showsMakerDisclaimer(line)"
      class="allergen-declaration__disclaimer small-text"
    >
      <strong>Disclaimer:</strong>
      {{ " " }}
      <span class="allergen-declaration__sentence">{{ makerDisclaimer(line, makerName) }}</span>
    </p>
    <template v-else>
      <slot
        v-if="showAllergens"
        name="allergens"
      >
        <p class="allergen-declaration__allergens small-text">
          <strong>Allergens:</strong>
          {{ effectiveAllergenText(declared) }}
        </p>
      </slot>
      <div class="allergen-declaration__dietaries">
        <strong class="small-text">Dietaries:</strong>
        <ProductAllergens
          :product="declared ?? undefined"
          :show-tooltip="tooltip"
          :show-empty-state="true"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.allergen-declaration {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  &__disclaimer,
  &__allergens {
    margin: 0;
  }

  // main.scss sizes any span inside a p at 1rem !important; the sentence takes its line's size.
  &__sentence {
    font-size: inherit !important;
    line-height: inherit !important;
  }

  &__dietaries {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-1);
  }
}
</style>
